import { Tooltip, Dropdown, MenuProps } from "antd";
import { RightOutlined } from "@ant-design/icons";
import cls from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useMutation } from "@tanstack/react-query";
import { isJiajiaClassApp } from "@/utils/app";
import styles from "./index.module.scss";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useRouteQuery from "@/hooks/useRouteQuery";

import {
  isWorkLeftPanelOpenAtom,
  todayWorksAtom,
  earlierWorksAtom,
  weekWorksAtom,
  topWorksAtom,
  recentLessonAtom,
} from "@/atom/drawBoard";
import { userInfoAtom } from "@/atom/user";
import BtnAdd from "@/components/BtnAdd";
import WorkItem from "@/components/WorkItem";
import {
  getControlPanelSettings,
  updateCourseStart,
} from "@/services/Course/courseApi";
import { fetchPublishShareWork, postDownlineShareWork } from "@/services/share";
import useUpdateCourseSettings from "@/hooks/useUpdateCourseSettings";
import useWork from "@/hooks/useWork";

const ControlPanel = () => {
  const { lessonId = "", shareId = "" } = useRouteQuery();
  const topWorks = useAtomValue(topWorksAtom);
  const weekWorks = useAtomValue(weekWorksAtom);
  const todayWorks = useAtomValue(todayWorksAtom);
  const earlierWorks = useAtomValue(earlierWorksAtom);
  let navigate = useNavigate();
  const [isWorkLeftPanelOpen, setIsWorkLeftPanelOpen] = useAtom(
    isWorkLeftPanelOpenAtom
  );
  const { updateLeftPanelStatus } = useUpdateCourseSettings();
  const userInfo = useAtomValue(userInfoAtom);
  const [recentLesson, setRecentLesson] = useAtom(recentLessonAtom);
  const [hasCourse, setHasCourse] = useState<boolean>(false);
  const [toolTipVisible, setTooltipVisible] = useState<boolean>(false);
  const [shareWorkList, setShareWorkList] = useState<any[]>();

  // 添加用于跟踪每个recentLesson项目是否需要tooltip的状态
  const [showTooltipMap, setShowTooltipMap] = useState<Record<number, boolean>>(
    {}
  );
  const titleRefs = useRef<Record<number, HTMLParagraphElement | null>>({});

  const isMobile = isJiajiaClassApp;

  const { filterWorks } = useWork();

  // useEffect(() => {
  //   async function getSettings() {
  //     let res = await getWorkPanelSettings();
  //     if (res?.code === 0 && res?.msg === "success") {
  //       filterWorks(res.data.works);
  //     }
  //   }
  //   getSettings();
  // }, []);

  useEffect(() => {
    if (!userInfo?.userId) return;

    async function getData() {
      let [res, resShareWork] = await Promise.all([
        getControlPanelSettings(),
        fetchPublishShareWork(),
      ]);
      if (res.code === 0 && res.msg === "success") {
        filterWorks(res.data.works);
        setRecentLesson(() => res.data.recentLesson);
        setHasCourse(res.data.hasCourse);
      }
      if (res.code === 0 && res.msg === "success") {
        setShareWorkList(resShareWork.data);
      }
    }

    getData();
  }, [userInfo?.userId]);

  const handleMenuClick: MenuProps["onClick"] = async (e) => {
    navigate(`/${e.key}`);
  };

  const menu: MenuProps = {
    items: [
      ...(!isJiajiaClassApp
        ? [
            {
              key: "vipshow",
              label: <span style={{ fontSize: 16 }}>订阅LoopUpAI</span>,
            },
          ]
        : []),
      {
        key: "personalinfo",
        label: <span style={{ fontSize: 16 }}>个人信息</span>,
      },
      {
        key: "audiocheck",
        label: <span style={{ fontSize: 16 }}>语音测试</span>,
      },
      ...(!isJiajiaClassApp
        ? [
            {
              key: "download",
              label: <span style={{ fontSize: 16 }}>下载软件</span>,
            },
          ]
        : []),
    ],
    onClick: handleMenuClick,
  };

  const goToLesson = async (lesson: any) => {
    if (!userInfo?.userId) {
      let loc = `/enter?preLocation=/courseinfo?courseId=${lesson.id}`;

      navigate(loc);
      return;
    } else {
      if (lesson.status !== 0 && lesson.status !== 3) {
        await updateCourseStart(lesson.id);
      }

      if (lesson?.status === 3) {
        navigate(`/courseinfo?courseId=${lesson?.courseId}`);
        return;
      }
      if (lesson.seriesId === 6) {
        // setIsWorkLeftPanelOpen(false);
        let url = `/course?lessonId=${lesson.id}`;
        navigate(url);
        return;
      } else if (lesson.seriesId === 14) {
        //数学
        let url = `/course?lessonId=${lesson.id}&type=m`;
        navigate(url);
        return;
      } else if (
        lesson.seriesId === 13 ||
        lesson.seriesId === 16 ||
        lesson.seriesId === 17
      ) {
        //数学
        let url = `/course?lessonId=${lesson.id}&type=m&courseType=s`;
        navigate(url);
        return;
      } else if (lesson.seriesId === 4) {
        //时文精选
        let url = `/course?lessonId=${lesson.id}&type=m&courseType=english`;
        navigate(url);
        return;
      } else if (lesson.seriesId === 15) {
        //自然拼读
        let url = `/course?lessonId=${lesson.id}&type=er&courseType=english`;
        navigate(url);
        return;
      } else {
        let url = `/teach/course/${lesson.courseAlias}`;
        if (lesson.status === 2 || lesson.status === 4) {
          url = `/teach/reviewcourse/${lesson.courseAlias}?lessonId=${lesson.id}&courseId=${lesson.courseId}`;
        }
        // window.open(url);
        window.location.href = url;
      }
    }
  };

  const mutationPostDownlineShareWork = useMutation({
    mutationFn: postDownlineShareWork,
    onSuccess: (resData, params) => {
      const { code } = resData;
      if (code === 0) {
        setShareWorkList((prev) => {
          const newList = prev?.filter(
            (item) => item.shareId !== params.shareId
          );
          if (params.shareId === shareId) {
            if (newList?.length === 0) {
              navigate(
                `/work?currentWorkId=${
                  topWorks?.[0]?.workId ||
                  weekWorks?.[0]?.workId ||
                  todayWorks?.[0]?.workId ||
                  earlierWorks?.[0]?.workId
                }`
              );
            } else {
              navigate(`/shareWork?shareId=${shareWorkList?.[0]?.shareId}`);
            }
          }
          return newList;
        });
      }
    },
  });

  const deleteShareApp = (shareId: any) => {
    mutationPostDownlineShareWork.mutate({
      shareId: shareId,
      action: "delete",
    });
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.headerWrapper}
        style={{ position: "absolute", width: "100%", padding: "15px", top: 0 }}
      >
        <div
          className={styles.logo}
          onClick={() => {
            window.location.href = `/`;
          }}
        ></div>
        {isWorkLeftPanelOpen && (
          <Tooltip
            placement="bottom"
            title="收起侧栏"
            open={toolTipVisible}
            onOpenChange={setTooltipVisible}
          >
            <button
              className={styles.leftPanelSwitch}
              onClick={() => {
                setIsWorkLeftPanelOpen(false);
                updateLeftPanelStatus(false);
              }}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
            ></button>
          </Tooltip>
        )}
      </div>
      <div className={styles.contentWrapper} style={{ height: "90vh" }}>
        <div className={styles.workList}>
          <BtnAdd isLeftPanel={true} />
          {hasCourse && (
            <div
              className={cls({
                [styles.categoryBlock]: true,
                [styles.selected]:
                  window.location.pathname.toLowerCase() === "/mycourses",
              })}
              onClick={() => {
                navigate("/myCourses");
              }}
            >
              <img className={styles.myCoursesIcon} alt="myCoursesIcon"></img>
              <p className={styles.category}>我的课程</p>
              <RightOutlined className={styles.rightIcon} />
            </div>
          )}

          <div
            className={cls({
              [styles.categoryBlock]: true,
              [styles.selected]:
                window.location.pathname.includes("allcourses"),
            })}
            onClick={() => {
              navigate("/allcourses");
            }}
          >
            <img className={styles.allCoursesIcon} alt="allCoursesIcon"></img>
            <p className={styles.category}>全部课程</p>

            <RightOutlined className={styles.rightIcon} />
          </div>

          <div style={{ marginBottom: "10px" }}>
            {recentLesson.map((item: any, index: number) => {
              // 创建ref处理函数
              const setTitleRef = (el: HTMLParagraphElement | null) => {
                if (el) {
                  titleRefs.current[index] = el;
                  // 检查文本是否溢出
                  const { scrollWidth, clientWidth } = el;
                  setShowTooltipMap((prev) => ({
                    ...prev,
                    [index]: scrollWidth > clientWidth && !isMobile,
                  }));
                }
              };

              return (
                <div
                  key={index.toString()}
                  className={cls({
                    [styles.categoryBlock]: true,
                    [styles.selected]: lessonId === item?.id?.toString(),
                  })}
                  onClick={() => goToLesson(item)}
                >
                  <img
                    className={styles.recentLessonIcon}
                    alt="recentLessonIcon"
                  ></img>
                  <Tooltip
                    title={showTooltipMap[index] ? item.title : ""}
                    placement="top"
                  >
                    <p ref={setTitleRef} className={styles.category}>
                      {item.title}
                    </p>
                  </Tooltip>
                </div>
              );
            })}
          </div>

          {shareWorkList && shareWorkList?.length > 0 && (
            <div className={styles.workBlock}>
              <p className={styles.title}>应用</p>
              {shareWorkList.map((item: any, index: number) => {
                return (
                  <WorkItem
                    key={index}
                    item={item}
                    type="shareApp"
                    onDelete={() => deleteShareApp(item.shareId)}
                  ></WorkItem>
                );
              })}
            </div>
          )}

          {topWorks.length > 0 && (
            <div className={styles.workBlock}>
              <p className={styles.title}>置顶</p>
              {topWorks.map((item: any, index: number) => {
                return <WorkItem key={index} item={item}></WorkItem>;
              })}
            </div>
          )}

          {todayWorks.length > 0 && (
            <div className={styles.workBlock}>
              <p className={styles.title}>今天</p>
              {todayWorks.map((item: any, index: number) => {
                return <WorkItem key={index} item={item}></WorkItem>;
              })}
            </div>
          )}

          {weekWorks.length > 0 && (
            <div className={styles.workBlock}>
              <p className={styles.title}>本周</p>
              {weekWorks.map((item: any, index: number) => {
                return <WorkItem key={index} item={item}></WorkItem>;
              })}
            </div>
          )}

          {earlierWorks?.length > 0 && (
            <div className={styles.workBlock}>
              <p className={styles.title}>更早</p>
              {earlierWorks.map((item: any, index: number) => {
                return <WorkItem key={index} item={item}></WorkItem>;
              })}
            </div>
          )}
        </div>
        <Dropdown
          trigger={["click"]}
          menu={menu}
          placement="bottomRight"
          align={{
            offset: [65, -7], // 第一个值控制水平偏移，第二个值控制垂直偏移
          }}
          overlayClassName={styles.dropDownMenu}
        >
          <div className={styles.avatarBlock}>
            <img
              src={userInfo?.avatar}
              className={styles.avatar}
              alt={userInfo?.nickname}
            />
            <p className={styles.category}>{userInfo?.nickname}</p>
            <div className={styles.dropDown}>
              <div className={styles.workSetting}></div>
            </div>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default ControlPanel;
